// data/sources.js — every upstream the browser is allowed to call, and the cache in front of it.
//
// CONTRACT (tests/test_contract.mjs):
//   export const SOURCES
//   export async function load(id)
//   export function status()
//
// Additions beyond the contract, all documented in the build report:
//   export function onUpdate(fn)      — background revalidation is useless without a notification
//   export function forget(id)        — drop one cached copy (used by the status panel's retry)
//   export function forgetIndex()     — drop the cached snapshot manifest, so a retry re-reads it
//   export function harvestStatus()   — what the manifest last said: generated_at and the counts
//
// THE READ ORDER (internal spec 0078, stage r14-data; data/readorder.js says it once, tests/test_read_order.mjs holds it):
//   1. OUR SNAPSHOT -- data/v1/<id>.json, filled hourly by the Lambda -- fresh or stale, drawn at once;
//   2. the ORIGINAL API only when there is no snapshot, or behind a snapshot that is past its own valid_until;
//   3. the BROWSER'S OWN COPY (below) only when both gave nothing, and never over a newer snapshot: a browser
//      that holds a copy older than the manifest's row reads the snapshot even inside the source's cadence.
//
// Rules this file exists to enforce:
//
//  * CelesTrak publishes GP data once every two hours and allows ONE download per file per
//    cycle. An early re-fetch answers 403 "GP data has not updated since your last successful
//    download", and fifty errors in two hours gets the client firewalled. So: a cadence with a
//    margin, ONE in-flight request per source, and a 403 recorded as `not yet updated` and
//    NEVER retried inside the cadence window. The attempt timestamp — not the success
//    timestamp — is what gates the next fetch, so a failing source cannot become a retry loop.
//  * Launch Library 2 allows 15 requests per hour per IP (measured from its own
//    /api-throttle/ endpoint). At most one call per page load, 2 h cadence.
//  * SNAPSHOT FIRST (spec 0003 amendment 1, §3 and §4). The harvester writes /data/v1/index.json
//    and one /data/v1/<registry-id>.json per source with the upstream body VERBATIM, so the
//    parsers do not change — only the transport does. A source is read from our snapshot when
//    the manifest says one exists. When it does not (no index, no row, a refused or failed
//    harvest, an unknown schema) a `browser: true` row falls back to the direct fetch below,
//    recorded `via: 'live'`; a `browser: false` row says "could not look" and makes NO upstream
//    request. Nothing is invented, and the direct path stays: it is the safety net.
//  * A SNAPSHOT PAST ITS OWN valid_until IS A SAVED COPY, NOT THE ANSWER. It is drawn at once, and
//    a `browser: true` row also asks its publisher behind it; a newer live answer replaces it and
//    onUpdate() says so, so the layers redraw. Without this a snapshot uploaded once (from a
//    laptop, 2026-09-22, while the harvester stays unprovisioned) would have frozen the map at the
//    moment of the upload: snapshot-first used to mean snapshot-only.
//  * Nothing here throws. Ever. A source that cannot be reached yields ok:false and the app
//    keeps its last good copy.
//  * localStorage is absent in private mode on some browsers and throws on access in others,
//    so every read and write is wrapped and there is an in-memory mirror behind it.
//
// TIME: this module uses wall-clock Date.now() for cache bookkeeping ONLY. That is deliberate
// and is not a violation of the one-clock rule: the app's clock can be scrubbed to 2035, and
// an HTTP cache that honoured a scrubbed clock would hammer CelesTrak. Nothing in this file is
// drawn. Ages shown in the status panel are wall-clock ages, which is what they mean.

import { decide, lastResort, newerThanHeld, snapshotNewerThanHeld } from './readorder.js';

const PREFIX = 'sr.v1.';
// THE LARGEST PAYLOAD KEPT IN localStorage, in characters. Chrome holds 5 241 856 characters in
// localStorage for the WHOLE origin (measured 2026-09-18), and two feeds are bigger than that on
// their own: CelesTrak `active` at 6.98 M and the Starlink supplemental at 5.07 M. Anything above
// this goes to Cache Storage instead (see BULK below), and the ceiling leaves every other source
// -- about 1.4 M between them -- room to be cached next to each other.
const LOCAL_MAX_CHARS = 1.5 * 1024 * 1024;
const BULK_CACHE = PREFIX + 'bulk';
/** The payload object last written to the bulk store per source, so an attempt stamp does not rewrite 7 MB. */
const bulkSaved = new Map();
const HOUR = 3600 * 1000;
const MINUTE = 60 * 1000;

const NO_CORS_REASON =
  'This publisher does not let a page on another website read it (it sends no CORS header), so a ' +
  'browser cannot. Bundled sample data stands in for it.';

// Where the harvester puts its files (amendment 1 §3): beside the page, on the site's own origin,
// so the read needs CORS from nobody. RELATIVE TO THE PAGE, like every other path the app uses
// (2026-10-05, internal issue 369): it was the absolute `/data/v1/`, and a school that served the
// folder at http://server/space-radar/ got an app that never found its saved copy. The app only
// ever runs at its index, so the page's folder is the site's folder.
// tests/test_relative_urls.mjs refuses an absolute site path anywhere under site/.
const SNAPSHOT_BASE = 'data/v1/';
const SNAPSHOT_INDEX_URL = SNAPSHOT_BASE + 'index.json';
const SNAPSHOT_SCHEMA = 1;
// The manifest is re-read at most this often, and only when a source is due or the page is new.
// It is the shortest cadence any source has (dsn-now, 5 min), so a due source never reads a
// manifest older than its own cadence; the HTTP cache (max-age=60) does the rest.
const INDEX_CADENCE_MS = 5 * MINUTE;
// A copy held inside its cadence is re-read when the manifest names a newer snapshot (see load()), but not
// more often than this per source, so a snapshot file that cannot be read is not asked for on every call.
const RECHECK_MS = MINUTE;
// WHEN THERE IS NOTHING TO SHOW, TRY AGAIN SOON. A source's cadence is how long a GOOD copy is
// allowed to stand -- three hours for most CelesTrak groups, six for `active`. Applying it to a
// source that has NO data was the wrong reading of it: a visitor who arrives while CelesTrak's
// two-hour window is closed for their address (an office, a campus, a mobile carrier) saw an empty
// layer, and the app then refused to look again for three hours, long after the window reopened.
//
// So an empty source retries on a floor of fifteen minutes, doubling per consecutive failure up to
// the source's own cadence: 15, 30, 60, 120 minutes. The backoff is the part that keeps this safe.
// CelesTrak firewalls a client that makes fifty errors in two hours, and five sources retrying
// flat out every fifteen minutes would be forty.
const EMPTY_RETRY_MS = 15 * MINUTE;
const EMPTY_RETRY_STEPS = 4;
// How long a live upstream fetch may take before it is abandoned. See fetchLive().
const LIVE_TIMEOUT_MS = 20 * 1000;
// Manifest statuses under which a snapshot FILE exists to read. `not-due` is here on purpose:
// a 24 h source is due once in 48 runs and the file from the earlier run is still there.
// `refused`, `error` and `skipped` are not: §4 says a snapshot that errored is one we do not have.
const SNAPSHOT_USABLE = new Set(['ok', 'not-modified', 'not-due']);

/**
 * @typedef {Object} Source
 * @property {string}  id
 * @property {string}  label            short human name for the status panel
 * @property {string}  url
 * @property {number}  cadenceMs        never fetched again before this has passed
 * @property {number}  freshnessMaxMs   older than this and the app shows a stale stamp
 * @property {boolean} browser          MEASURED 2026-09-06, the CORS column of docs/data-sources.md:
 *                                      true when a page may read the host directly, which is what
 *                                      the app falls back to when our snapshot is missing
 * @property {string}  publisher        who to name in "read live from {publisher}"
 * @property {string}  attribution      the credit line the card must carry
 * @property {string}  kind             'json' | 'text' | 'xml'
 * @property {string}  [registryId]     the row in registry/sources.yaml this implements
 * @property {string}  [note]
 */

/** @type {Record<string, Source>} */
export const SOURCES = {
  // --- Earth orbit ------------------------------------------------------------------------
  'celestrak-stations': {
    id: 'celestrak-stations',
    registryId: 'celestrak-stations',
    label: 'CelesTrak — crewed stations',
    publisher: 'CelesTrak',
    url: 'https://celestrak.org/NORAD/elements/gp.php?GROUP=stations&FORMAT=json',
    cadenceMs: 3 * HOUR,
    freshnessMaxMs: 48 * HOUR,
    browser: true,
    kind: 'json',
    attribution: 'Orbital data: CelesTrak (T. S. Kelso)',
    note: '22 objects, 9.3 kB measured 2026-09-06.',
  },
  'celestrak-active': {
    id: 'celestrak-active',
    registryId: 'celestrak-active',
    label: 'CelesTrak — everything active',
    publisher: 'CelesTrak',
    url: 'https://celestrak.org/NORAD/elements/gp.php?GROUP=active&FORMAT=json',
    // The registry says 6 h for this file and 3 h is the site-wide floor; the slower of the
    // two wins, because this is the 6.9 MB one.
    cadenceMs: 6 * HOUR,
    freshnessMaxMs: 72 * HOUR,
    browser: true,
    kind: 'json',
    attribution: 'Orbital data: CelesTrak (T. S. Kelso)',
    note: '16 511 objects, 6 907 333 bytes measured 2026-09-06. Load only when asked.',
  },
  'celestrak-starlink': {
    id: 'celestrak-starlink',
    registryId: 'celestrak-supplemental-starlink',
    label: 'CelesTrak — Starlink (operator ephemerides)',
    publisher: 'CelesTrak',
    url: 'https://celestrak.org/NORAD/elements/supplemental/sup-gp.php?FILE=starlink&FORMAT=json',
    cadenceMs: 3 * HOUR,
    freshnessMaxMs: 24 * HOUR,
    browser: true,
    kind: 'json',
    attribution: 'Orbital data: CelesTrak (T. S. Kelso)',
    note: "SpaceX's own elements, hours fresher than the tracked catalogue. 11 129 objects, 5.1 MB.",
  },
  // TWO CUTS OF THE BIG FEEDS, published beside them (scripts/refresh-snapshots.sh). The first
  // screen draws 33 "satellites worth knowing" out of the 16 587-object active catalogue and the
  // Starlink trains out of the 11 136-object supplemental file; with the saved copy in /data/v1 a
  // first visit spent 1.9 MB of its 4.6 MB on those two files (measured on a phone, 2026-09-22). A
  // cut holds only what the layer selects; with no cut in the manifest, or once it is out of date,
  // the live URL below is the full file, exactly as before.
  //
  // A cut PAST ITS valid_until IS NOT REFRESHED BEHIND ITSELF (`cutOf`): its live URL is the whole
  // file, so asking it behind the copy spent the saving the cut exists for. MEASURED 2026-09-28,
  // with the saved copy six days old: every first visit asked CelesTrak for the 7 MB catalogue
  // twice (notable and geo) and the 5 MB Starlink file once, behind cuts already on screen. A
  // stale cut stays drawn and says its age; the next snapshot refresh replaces it. With no cut in
  // the manifest at all, the full live file is still the safety net.
  'celestrak-notable': {
    id: 'celestrak-notable',
    registryId: 'celestrak-notable',
    cutOf: 'celestrak-active',
    label: 'CelesTrak — satellites worth knowing (a cut of the active catalogue)',
    publisher: 'CelesTrak',
    url: 'https://celestrak.org/NORAD/elements/gp.php?GROUP=active&FORMAT=json',
    cadenceMs: 3 * HOUR,
    freshnessMaxMs: 24 * HOUR,
    browser: true,
    kind: 'json',
    attribution: 'Orbital data: CelesTrak (T. S. Kelso)',
    note: 'The active catalogue cut to the satellites the hand-kept list names, when our copy has the cut.',
  },
  'celestrak-geo': {
    id: 'celestrak-geo',
    registryId: 'celestrak-geo',
    cutOf: 'celestrak-active',
    label: 'CelesTrak — the geostationary ring (a cut of the active catalogue)',
    publisher: 'CelesTrak',
    url: 'https://celestrak.org/NORAD/elements/gp.php?GROUP=active&FORMAT=json',
    cadenceMs: 3 * HOUR,
    freshnessMaxMs: 24 * HOUR,
    browser: true,
    kind: 'json',
    attribution: 'Orbital data: CelesTrak (T. S. Kelso)',
    note: 'The active catalogue cut to the ring (data/parsers.js GEO_RING) when our copy has the cut: ' +
      'about 900 rows, 100 kB, instead of the 7 MB file.',
  },
  'celestrak-starlink-recent': {
    id: 'celestrak-starlink-recent',
    registryId: 'celestrak-starlink-recent',
    cutOf: 'celestrak-supplemental-starlink',
    label: 'CelesTrak — recent Starlink launches (a cut of the operator file)',
    publisher: 'CelesTrak',
    url: 'https://celestrak.org/NORAD/elements/supplemental/sup-gp.php?FILE=starlink&FORMAT=json',
    cadenceMs: 3 * HOUR,
    freshnessMaxMs: 24 * HOUR,
    browser: true,
    kind: 'json',
    attribution: 'Orbital data: CelesTrak (T. S. Kelso)',
    note: 'SpaceX\'s file cut to its latest launches, when our copy has the cut: the trains are drawn from those.',
  },
  'celestrak-visual': {
    id: 'celestrak-visual',
    label: 'CelesTrak — bright enough to see',
    publisher: 'CelesTrak',
    url: 'https://celestrak.org/NORAD/elements/gp.php?GROUP=visual&FORMAT=json',
    cadenceMs: 3 * HOUR,
    freshnessMaxMs: 48 * HOUR,
    browser: true,
    kind: 'json',
    attribution: 'Orbital data: CelesTrak (T. S. Kelso)',
    note: '157 objects, 65 kB measured 2026-09-06. The calm default: everything here is ' +
      'visible to the naked eye from a dark-enough place.',
  },
  'celestrak-last30': {
    id: 'celestrak-last30',
    label: 'CelesTrak — launched in the last 30 days',
    publisher: 'CelesTrak',
    url: 'https://celestrak.org/NORAD/elements/gp.php?GROUP=last-30-days&FORMAT=json',
    cadenceMs: 3 * HOUR,
    freshnessMaxMs: 48 * HOUR,
    browser: true,
    kind: 'json',
    attribution: 'Orbital data: CelesTrak (T. S. Kelso)',
    note: '264 objects, 112 kB measured 2026-09-06.',
  },

  // The whole catalogue: one line for everything ever tracked in orbit, with the shape of its
  // orbit and no elements (data/satcat.js). Read only when the debris view is opened.
  'celestrak-satcat': {
    id: 'celestrak-satcat',
    registryId: 'celestrak-satcat',
    label: 'CelesTrak — the satellite catalogue',
    publisher: 'CelesTrak',
    url: 'https://celestrak.org/pub/satcat.csv',
    cadenceMs: 24 * HOUR,
    freshnessMaxMs: 168 * HOUR,
    browser: true,
    kind: 'text',
    attribution: 'Orbital data: CelesTrak (T. S. Kelso)',
    note: '70 813 lines, 6 751 364 bytes measured 2026-09-28. Load only when asked.',
  },

  // --- Launches ---------------------------------------------------------------------------
  'll2-upcoming': {
    id: 'll2-upcoming',
    registryId: 'll2-upcoming',
    label: 'Launch Library 2 — upcoming launches',
    publisher: 'The Space Devs',
    url: 'https://ll.thespacedevs.com/2.3.0/launches/upcoming/?limit=30&mode=detailed',
    cadenceMs: 2 * HOUR,
    freshnessMaxMs: 12 * HOUR,
    browser: true,
    kind: 'json',
    attribution: 'Launch data by The Space Devs',
    note: '15 requests per hour per IP, measured from the API\'s own /api-throttle/ endpoint. ' +
      'At most one call per page load. 30 detailed launches, 982 kB.',
  },

  // --- Deep space (the live one) -----------------------------------------------------------
  'dsn-now': {
    id: 'dsn-now',
    registryId: 'dsn-now',
    label: 'NASA DSN Now',
    publisher: 'NASA',
    url: 'https://eyes.nasa.gov/dsn/data/dsn.xml',
    cadenceMs: 5 * MINUTE,
    freshnessMaxMs: 30 * MINUTE,
    browser: true,
    kind: 'xml',
    attribution: 'NASA Deep Space Network',
    note: 'Unofficial feed behind a public page: no SLA, and it may change shape without ' +
      'notice. A link older than 30 minutes is not drawn — it would be a lie about now.',
  },

  // --- Space weather ------------------------------------------------------------------------
  'swpc-kp': {
    id: 'swpc-kp',
    registryId: 'swpc-kp',
    label: 'NOAA SWPC — planetary K index',
    publisher: 'NOAA SWPC',
    url: 'https://services.swpc.noaa.gov/products/noaa-planetary-k-index-forecast.json',
    cadenceMs: 15 * MINUTE,
    freshnessMaxMs: 6 * HOUR,
    browser: true,
    kind: 'json',
    attribution: 'Space weather: NOAA SWPC',
    note: '6.9 kB, 81 rows measured 2026-09-06.',
  },

  // --- Who is aboard -------------------------------------------------------------------------
  // registry/sources.yaml open-notify-astros says why: CORS allows a page to read this, but the
  // API has no https endpoint at all, and this app is served over https, so a browser here hits
  // mixed-content, not a CORS refusal. The harvester's plain-HTTP fetch is unaffected; the browser
  // reads the saved copy, snapshot-only.
  'open-notify-astros': {
    id: 'open-notify-astros',
    registryId: 'open-notify-astros',
    label: 'Open Notify — astronauts aboard',
    publisher: 'Open Notify',
    url: 'http://api.open-notify.org/astros.json',
    cadenceMs: 1 * HOUR,
    freshnessMaxMs: 24 * HOUR,
    browser: false,
    kind: 'json',
    attribution: 'Who is in space: Open Notify',
    note: 'No https endpoint at all (TLS refused), so a page served over https cannot reach it ' +
      'even though it sends Access-Control-Allow-Origin `*`. Read from our snapshot only.',
  },

  // Launch Library 2's stations, crew and past launches (registry/sources.yaml says the terms and
  // the budget). Snapshot-only on purpose: a visitor never spends the publisher's 15 calls an hour.
  'll2-stations': {
    id: 'll2-stations', registryId: 'll2-stations', label: 'Launch Library 2 — stations and docked vehicles', publisher: 'The Space Devs',
    url: 'https://ll.thespacedevs.com/2.3.0/space_stations/?status=1&mode=detailed',
    cadenceMs: 6 * HOUR, freshnessMaxMs: 48 * HOUR, browser: false, kind: 'json',
    attribution: 'Launch data by The Space Devs',
  },
  'll2-astronauts': {
    id: 'll2-astronauts', registryId: 'll2-astronauts', label: 'Launch Library 2 — people in space', publisher: 'The Space Devs',
    url: 'https://ll.thespacedevs.com/2.3.0/astronauts/?in_space=true&limit=30',
    cadenceMs: 6 * HOUR, freshnessMaxMs: 48 * HOUR, browser: false, kind: 'json',
    attribution: 'Launch data by The Space Devs',
  },
  'll2-previous': {
    id: 'll2-previous', registryId: 'll2-previous', label: 'Launch Library 2 — launches that happened', publisher: 'The Space Devs',
    url: 'https://ll.thespacedevs.com/2.3.0/launches/previous/?limit=30',
    cadenceMs: 6 * HOUR, freshnessMaxMs: 48 * HOUR, browser: false, kind: 'json',
    attribution: 'Launch data by The Space Devs',
  },

  // --- Weather on Earth ---------------------------------------------------------------------
  // registry/sources.yaml gdacs-tc says why this URL, this cadence and this licence.
  'gdacs-tc': {
    id: 'gdacs-tc',
    registryId: 'gdacs-tc',
    label: 'GDACS — tropical cyclones',
    publisher: 'GDACS',
    url: 'https://www.gdacs.org/gdacsapi/api/events/geteventlist/SEARCH?eventlist=TC&alertlevel=Green;Orange;Red&pageSize=20',
    cadenceMs: 1 * HOUR,
    freshnessMaxMs: 12 * HOUR,
    browser: true,
    kind: 'json',
    attribution: 'Tropical cyclones: GDACS, European Commission Joint Research Centre (CC BY 4.0)',
    note: '27.6 kB, the newest 20 storms, measured 2026-09-28. GDACS calls its information "purely indicative".',
  },

  // Three feeds the page used to read straight from their publishers (internal #525, #553). Each has a saved
  // copy now, which load() reads first; the publisher is the fallback (`browser: true`). registry/sources.yaml
  // says why these URLs, cadences and terms. data/eonet.js and data/sunregions.js name the same URLs.
  'eonet-fires': {
    id: 'eonet-fires',
    registryId: 'eonet-fires',
    label: 'NASA EONET — fires, last 30 days',
    publisher: 'NASA Earth Observatory',
    url: 'https://eonet.gsfc.nasa.gov/api/v3/events?status=open&category=wildfires&days=30',
    cadenceMs: 1 * HOUR,
    freshnessMaxMs: 12 * HOUR,
    browser: true,
    kind: 'json',
    attribution: 'Fires, volcanoes and sea ice: NASA EONET',
    note: '69 kB for both lists before they were ours, measured 2026-10-09.',
  },
  'eonet-volcanoes-ice': {
    id: 'eonet-volcanoes-ice',
    registryId: 'eonet-volcanoes-ice',
    label: 'NASA EONET — volcanoes and sea ice',
    publisher: 'NASA Earth Observatory',
    url: 'https://eonet.gsfc.nasa.gov/api/v3/events?status=open&category=volcanoes,seaLakeIce',
    cadenceMs: 1 * HOUR,
    freshnessMaxMs: 12 * HOUR,
    browser: true,
    kind: 'json',
    attribution: 'Fires, volcanoes and sea ice: NASA EONET',
  },
  'swpc-solar-regions': {
    id: 'swpc-solar-regions',
    registryId: 'swpc-solar-regions',
    label: 'NOAA SWPC — sunspot groups',
    publisher: 'NOAA Space Weather Prediction Center',
    url: 'https://services.swpc.noaa.gov/json/solar_regions.json',
    cadenceMs: 1 * HOUR,
    freshnessMaxMs: 12 * HOUR,
    browser: true,
    kind: 'json',
    attribution: 'Sunspot groups: NOAA SWPC',
  },
  // Flares and coronal mass ejections for the Sun close up (data/sunactivity.js, spec 0073). The
  // flare list falls back to SWPC when we hold no copy; NASA's CME list is read from our copy only.
  'swpc-edited-events': {
    id: 'swpc-edited-events',
    registryId: 'swpc-edited-events',
    label: 'NOAA SWPC — solar flares',
    publisher: 'NOAA Space Weather Prediction Center',
    url: 'https://services.swpc.noaa.gov/json/edited_events.json',
    cadenceMs: 1 * HOUR,
    freshnessMaxMs: 12 * HOUR,
    browser: true,
    kind: 'json',
    attribution: 'Solar flares: NOAA SWPC',
  },
  'donki-cme-analysis': {
    id: 'donki-cme-analysis',
    registryId: 'donki-cme-analysis',
    label: 'NASA DONKI — coronal mass ejections',
    publisher: 'NASA M2M Space Weather Analysis Office and CCMC',
    url: 'https://ccmc.gsfc.nasa.gov/DONKI-API/get/CMEAnalysis',
    cadenceMs: 3 * HOUR,
    freshnessMaxMs: 24 * HOUR,
    browser: false,
    kind: 'json',
    attribution: 'Coronal mass ejections: NASA DONKI (M2M Space Weather Analysis Office, CCMC)',
    // 48.1 kB, the analyses of the last thirty days, measured 2026-10-12.
  },
  // 0076 W1-d: the Sun's face of the day (scene/sunimage.js). The archive sends no CORS header: our saved copy only.
  'sdo-aia-171': {
    id: 'sdo-aia-171',
    registryId: 'sdo-aia-171',
    label: 'NASA SDO — the Sun in extreme ultraviolet (AIA 171 Å)',
    publisher: 'NASA Solar Dynamics Observatory',
    url: 'https://sdo.gsfc.nasa.gov/assets/img/browse/',
    cadenceMs: 3 * HOUR,
    freshnessMaxMs: 72 * HOUR,
    browser: false,
    kind: 'json',
    attribution: 'The Sun\'s face of the day: courtesy of NASA/SDO and the AIA, EVE, and HMI science teams',
    // 251.5 kB saved (a 188 kB picture in base64), the newest of the day, measured 2026-10-15.
  },

  // The wind overlay's field (scene/wind.js, data/wind.js; internal #552). Read only from our saved copy:
  // `browser: false`, registry/sources.yaml `wind` says why. Nothing calls load() for it; the Earth data
  // panel asks snapshotAvailable('wind') and offers the wind only when a copy exists.
  wind: {
    id: 'wind',
    registryId: 'wind',
    label: 'Wind — NOAA GFS, ten metres up',
    publisher: 'PacIOOS (University of Hawaii)',
    url: 'https://pae-paha.pacioos.hawaii.edu/erddap/griddap/ncep_global.html',
    cadenceMs: 3 * HOUR,
    freshnessMaxMs: 12 * HOUR,
    browser: false,
    kind: 'json',
    attribution: 'Wind: NOAA/NCEP Global Forecast System, through PacIOOS ERDDAP (University of Hawaii)',
    note: '168.8 kB, 37 x 72 points for one forecast hour, measured 2026-10-09.',
  },

  // The ocean currents overlay's two fields (scene/currents.js, data/currents.js). Read only from our
  // saved copies: `browser: false`, registry/sources.yaml says why (no CORS header, megabytes unpacked).
  // Nothing calls load() for them; the Earth data panel asks snapshotAvailable() for both and offers
  // "Ocean currents" only when both exist.
  'ocean-currents': {
    id: 'ocean-currents',
    registryId: 'ocean-currents',
    label: 'Ocean currents — NOAA CoastWatch, from satellite altimetry',
    publisher: 'NOAA CoastWatch',
    url: 'https://coastwatch.noaa.gov/erddap/griddap/noaacwBLENDEDNRTcurrentsDaily.html',
    cadenceMs: 24 * HOUR,
    freshnessMaxMs: 96 * HOUR,
    browser: false,
    kind: 'json',
    attribution: 'Ocean currents: NOAA CoastWatch, from satellite altimetry (Sentinel data courtesy of the Copernicus Programme; generated using AVISO+ products)',
    note: '262 kB saved (4.4 MB asked for), 180 x 360 points for one day, measured 2026-10-12.',
  },
  'ocean-temperature': {
    id: 'ocean-temperature',
    registryId: 'ocean-temperature',
    label: 'Sea temperature — NOAA OISST v2.1',
    publisher: 'NOAA NCEI, through NOAA CoastWatch West Coast',
    url: 'https://coastwatch.pfeg.noaa.gov/erddap/griddap/ncdcOisst21NrtAgg.html',
    cadenceMs: 24 * HOUR,
    freshnessMaxMs: 96 * HOUR,
    browser: false,
    kind: 'json',
    attribution: 'Sea temperature: NOAA NCEI OISST v2.1, through NOAA CoastWatch West Coast ERDDAP',
    note: '184 kB saved (3.8 MB asked for), 180 x 360 points for one day, measured 2026-10-12.',
  },

  // --- Sources a browser cannot reach -------------------------------------------------------
  // Present so the status panel can say "could not look" about them by name, which is a
  // different answer from "stale" and a very different answer from "fine". Every one of these
  // returned 200 with NO Access-Control-Allow-Origin header at all when measured.
  'jpl-sbdb-neo': {
    id: 'jpl-sbdb-neo',
    registryId: 'jpl-sbdb-neo',
    label: 'JPL Small-Body Database — near-Earth asteroids',
    publisher: 'NASA/JPL',
    url: 'https://ssd-api.jpl.nasa.gov/sbdb_query.api',
    cadenceMs: 24 * HOUR,
    freshnessMaxMs: 168 * HOUR,
    browser: false,
    kind: 'json',
    attribution: 'Orbits: NASA/JPL Small-Body Database',
    note: NO_CORS_REASON + ' Read from our snapshot; sampleAsteroids() stands in only when that is missing.',
  },
  'jpl-sbdb-comets': {
    id: 'jpl-sbdb-comets',
    registryId: 'jpl-sbdb-comets',
    label: 'JPL Small-Body Database — comet elements',
    publisher: 'NASA/JPL',
    url: 'https://ssd-api.jpl.nasa.gov/sbdb_query.api',
    cadenceMs: 24 * HOUR,
    freshnessMaxMs: 168 * HOUR,
    browser: false,
    kind: 'json',
    attribution: 'Orbits: NASA/JPL Small-Body Database',
    note: NO_CORS_REASON + ' Replaces the Minor Planet Center’s CometEls.txt, which did ' +
      'allow a direct fetch; JPL does not, so the comets layer is snapshot-only and shows ' +
      'nothing on a page loaded before the harvester’s first run.',
  },
  'nasa-exoplanet-archive': {
    id: 'nasa-exoplanet-archive',
    registryId: 'nasa-exoplanet-archive',
    label: 'NASA Exoplanet Archive — confirmed planets',
    publisher: 'Caltech/IPAC for NASA',
    url: 'https://exoplanetarchive.ipac.caltech.edu/TAP/sync?query=select+pl_name,hostname,ra,dec,sy_dist,pl_rade,pl_bmasse,pl_orbper,disc_year,discoverymethod,st_teff,st_rad,st_spectype+from+pscomppars&format=csv',
    cadenceMs: 168 * HOUR,
    freshnessMaxMs: 720 * HOUR,
    browser: false,
    kind: 'text',
    attribution: 'Exoplanets: NASA Exoplanet Archive (Caltech/IPAC), DOI 10.26133/NEA13',
    note: NO_CORS_REASON + ' Read from our snapshot; without one the layer shows the dated copy bundled in site/data/exoplanets.csv and says so.',
  },
  'jpl-cad': {
    id: 'jpl-cad',
    registryId: 'jpl-cad',
    label: 'JPL CNEOS — close approaches',
    publisher: 'NASA/JPL',
    url: 'https://ssd-api.jpl.nasa.gov/cad.api?dist-max=10LD&date-min=now&date-max=%2B60&sort=date&diameter=true&fullname=true',
    cadenceMs: 24 * HOUR,
    freshnessMaxMs: 168 * HOUR,
    browser: false,
    kind: 'json',
    attribution: 'Close approaches: NASA/JPL CNEOS',
    // Which asteroids are "passing by" this month. Paired with jpl-sbdb-neo, which has their
    // orbits; parsers.js parseNeoApproaches joins the two.
    note: NO_CORS_REASON + ' Read from our snapshot; without it the asteroid layer shows its stand-ins.',
  },
  'horizons-deep-space': {
    id: 'horizons-deep-space',
    registryId: 'horizons-deep-space',
    label: 'JPL Horizons — probe and telescope vectors',
    publisher: 'NASA/JPL',
    url: 'https://ssd.jpl.nasa.gov/api/horizons.api',
    cadenceMs: 24 * HOUR,
    freshnessMaxMs: 168 * HOUR,
    browser: false,
    kind: 'text',
    attribution: 'Ephemerides: JPL Horizons',
    note: NO_CORS_REASON + ' See data/sample.js sampleDeepSpace().',
  },
  'space-track-tip': {
    id: 'space-track-tip',
    registryId: 'space-track-tip',
    label: 'Space-Track — reentry windows',
    publisher: 'Space-Track',
    url: 'https://www.space-track.org/basicspacedata/query/class/tip/format/json',
    cadenceMs: 6 * HOUR,
    freshnessMaxMs: 48 * HOUR,
    browser: false,
    kind: 'json',
    attribution: 'Reentry predictions: 18 SDS via Space-Track.org',
    note: 'Needs a login, which a public page cannot hold. It is also the only public source ' +
      'of reentry windows, so v1 shows real past reentries instead of guessing at a live one. ' +
      'See data/sample.js sampleReentries().',
  },
};

// --- the cache -------------------------------------------------------------------------------

/**
 * @typedef {Object} Entry
 * @property {*}           data           last good payload, already parsed for json sources
 * @property {number|null} fetchedAt      wall ms of the last SUCCESS
 * @property {number|null} lastAttemptAt  wall ms of the last attempt, success or not
 * @property {string|null} lastError
 * @property {number|null} lastStatus     HTTP status of the last attempt
 * @property {string|null} via            'snapshot' | 'live': where `data` came from. null when
 *                                        there is none, or the copy predates this field
 * @property {number|null} validUntil     wall ms by which the harvester promised a fresher snapshot
 * @property {number|null} readAt         wall ms WE read the copy; fetchedAt is the upstream read
 * @property {string|null} reason         why the last attempt produced no data, as a code the
 *                                        status panel turns into words. null after a success
 * @property {string|null} snapshot       why our snapshot was not used on the last attempt, as a
 *                                        code; null when it was
 */

/** In-memory mirror. Also the whole cache when localStorage is unavailable. @type {Map<string, Entry>} */
const memory = new Map();

/** One in-flight request per source id. @type {Map<string, Promise>} */
const inflight = new Map();

/** @type {Set<Function>} */
const listeners = new Set();

let storageWorks = null; // null = untested, then true/false

function storage() {
  if (storageWorks === false) return null;
  try {
    const s = globalThis.localStorage;
    if (!s) {
      storageWorks = false;
      return null;
    }
    if (storageWorks === null) {
      const probe = PREFIX + '__probe';
      s.setItem(probe, '1');
      s.removeItem(probe);
      storageWorks = true;
    }
    return s;
  } catch {
    storageWorks = false;
    return null;
  }
}

function readEntry(id) {
  if (memory.has(id)) return memory.get(id);
  const s = storage();
  if (!s) return null;
  try {
    const raw = s.getItem(PREFIX + id);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const entry = {
      data: 'data' in parsed ? parsed.data : null,
      fetchedAt: numOrNull(parsed.fetchedAt),
      lastAttemptAt: numOrNull(parsed.lastAttemptAt) ?? numOrNull(parsed.fetchedAt),
      lastError: typeof parsed.lastError === 'string' ? parsed.lastError : null,
      lastStatus: numOrNull(parsed.lastStatus),
      via: parsed.via === 'snapshot' || parsed.via === 'live' ? parsed.via : null,
      validUntil: numOrNull(parsed.validUntil),
      readAt: numOrNull(parsed.readAt),
      reason: typeof parsed.reason === 'string' ? parsed.reason : null,
      snapshot: typeof parsed.snapshot === 'string' ? parsed.snapshot : null,
      bulk: parsed.bulk === true,
      failures: numOrNull(parsed.failures) || 0,
    };
    memory.set(id, entry);
    return entry;
  } catch {
    return null;
  }
}

function writeEntry(id, entry) {
  memory.set(id, entry);
  const s = storage();
  if (!s) return;
  // ONE PASS OVER THE PAYLOAD. A 7 MB feed is expensive to serialise -- it showed up in a CPU
  // profile of a phone boot -- so the data is stringified once and the result is reused for the
  // size decision, for localStorage, and for the Cache Storage body. The first version of this
  // stringified it up to three times.
  let dataText = null;
  try {
    dataText = entry.data == null ? null : JSON.stringify(entry.data);
  } catch {
    return; // a payload that cannot be serialised is not one we can cache
  }
  const big = dataText != null && dataText.length > LOCAL_MAX_CHARS;
  // A payload too big for localStorage is written to Cache Storage, and localStorage keeps a stub:
  // the timestamps, status and reason, with `bulk: true` where the data would be. The stub is what
  // gates the next fetch, so the two-hour rule holds across a reload whichever store has the data.
  const { data: _omit, ...meta } = entry;
  meta.bulk = big;
  let text;
  try {
    const metaText = JSON.stringify(meta);
    text = `{"data":${big || dataText == null ? 'null' : dataText}${metaText.length > 2 ? ',' + metaText.slice(1) : '}'}`;
  } catch {
    return;
  }
  if (big) {
    if (bulkSaved.get(id) !== entry.data) {
      bulkSaved.set(id, entry.data);
      bulkPut(id, dataText);
    }
  } else if (entry.bulk) {
    // A payload that shrank back under the ceiling: the stub no longer points anywhere.
    bulkDelete(id);
  }
  try {
    s.setItem(PREFIX + id, text);
  } catch {
    // Almost always a quota error. Drop the biggest other cached source and try once more,
    // then give up quietly and live off the in-memory mirror for this session. Only small entries
    // get here now: this used to run for the 7 MB `active` feed, which could never fit, so every
    // page load evicted the largest OTHER source -- `visual`, the 156 things bright enough to see
    // -- and then failed anyway. A reload inside CelesTrak's two hours found both gone.
    try {
      evictLargestExcept(s, id);
      s.setItem(PREFIX + id, text);
    } catch {
      /* the in-memory mirror is the cache now */
    }
  }
}

// --- the bulk store: Cache Storage, for payloads localStorage cannot hold ---------------------
//
// Cache Storage is per origin, sized as a share of the disk rather than at 5 MB, and available on
// every secure origin (https, and localhost). Where it is missing -- node, an insecure origin, a
// browser that refuses it -- the stub is written without its data and the source behaves as it did
// before this existed, except that it no longer evicts anybody else. Every call is wrapped: this
// file never throws.
function bulkStore() {
  try {
    return typeof globalThis.caches !== 'undefined' && globalThis.caches ? globalThis.caches : null;
  } catch {
    return null;
  }
}
function bulkKey(id) {
  const origin = (globalThis.location && globalThis.location.origin) || 'https://spaceradar.invalid';
  return new URL('/__sr-bulk/' + encodeURIComponent(id), origin).href;
}
/** @param {string} dataText the payload, already serialised by writeEntry -- serialised once. */
function bulkPut(id, dataText) {
  const c = bulkStore();
  if (!c) return Promise.resolve(false);
  return c.open(BULK_CACHE)
    .then((cache) => cache.put(bulkKey(id), new Response(dataText, { headers: { 'content-type': 'application/json' } })))
    .then(() => true, () => false);
}
function bulkDelete(id) {
  bulkSaved.delete(id);
  const c = bulkStore();
  if (!c) return Promise.resolve(false);
  return c.open(BULK_CACHE).then((cache) => cache.delete(bulkKey(id)), () => false).catch(() => false);
}
/** Fill a stub's data from the bulk store, into the in-memory mirror only. Never rejects. */
async function hydrate(id, entry) {
  if (!entry || !entry.bulk || entry.data != null) return entry;
  const c = bulkStore();
  if (!c) return entry;
  try {
    const cache = await c.open(BULK_CACHE);
    const hit = await cache.match(bulkKey(id));
    if (!hit) return entry;
    const data = await hit.json();
    const full = { ...entry, data };
    memory.set(id, full);
    bulkSaved.set(id, data);
    return full;
  } catch {
    return entry;
  }
}

function evictLargestExcept(s, keepId) {
  let victim = null;
  let victimSize = -1;
  for (let i = 0; i < s.length; i++) {
    const key = s.key(i);
    if (!key || !key.startsWith(PREFIX)) continue;
    if (key === PREFIX + keepId) continue;
    let size = 0;
    try {
      size = (s.getItem(key) || '').length;
    } catch {
      size = 0;
    }
    if (size > victimSize) {
      victim = key;
      victimSize = size;
    }
  }
  if (victim) s.removeItem(victim);
}

function numOrNull(v) {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function wallNow() {
  return Date.now();
}

function blankEntry() {
  return {
    data: null,
    fetchedAt: null,
    lastAttemptAt: null,
    lastError: null,
    lastStatus: null,
    via: null,
    validUntil: null,
    readAt: null,
    reason: null,
    snapshot: null,
    failures: 0,
  };
}

/**
 * How long a source with NO data waits before looking again: fifteen minutes, doubling per
 * consecutive failure, never longer than the source's own cadence.
 */
function emptyRetryMs(entry, src) {
  // The wait AFTER the first failure is the floor itself, so the exponent counts from zero there.
  const n = Math.min(Math.max(0, (entry.failures || 1) - 1), EMPTY_RETRY_STEPS);
  return Math.min(src.cadenceMs, EMPTY_RETRY_MS * 2 ** n);
}

// --- the public surface ------------------------------------------------------------------------

/**
 * Result of load(). Never rejects, never throws.
 * @typedef {Object} LoadResult
 * @property {boolean}     ok          true when `data` came from a successful fetch or a fresh cache
 * @property {*}           data        payload, or the last cached payload, or null
 * @property {number|null} fetchedAt   wall ms the payload was fetched
 * @property {boolean}     stale       payload is older than freshnessMaxMs, or there is none
 * @property {string|null} error       why the last attempt failed, in words
 * @property {boolean}     fromCache
 * @property {boolean}     revalidating a background fetch is running and onUpdate will fire
 * @property {string|null} via         'snapshot' | 'live' | null: the provenance of `data`
 * @property {number|null} validUntil  wall ms by which the harvester promised a fresher snapshot
 * @property {number|null} readAt      wall ms we read our copy (snapshot) or the upstream (live)
 * @property {boolean}     overdue     via snapshot and validUntil has passed. Said, never hidden;
 *                                     `stale` stays the source's own ladder (freshnessMaxMs)
 * @property {string|null} reason      why there is no data, as a code. 'no-route': our snapshot
 *                                     was unavailable AND a browser cannot read the host, so
 *                                     nothing was requested. 'live-failed': the direct read failed
 * @property {string|null} snapshot    why our snapshot was not used, as a code: 'no-index',
 *                                     'not-in-index', 'refused', 'error', 'skipped', 'unreadable'
 */

/**
 * Serve the cached copy instantly; revalidate behind it when the cadence has passed.
 * @param {string} id
 * @param {{force?: boolean, await?: boolean}} [opts]
 *        force: ignore the cadence (only for a human pressing retry — never on a timer)
 *        await: wait for the revalidation instead of returning the cached copy first
 * @returns {Promise<LoadResult>}
 */
export async function load(id, opts = {}) {
  const src = SOURCES[id];
  if (!src) {
    return {
      ok: false,
      data: null,
      fetchedAt: null,
      stale: true,
      error: `No source called "${id}".`,
      fromCache: false,
      revalidating: false,
      via: null,
      validUntil: null,
      readAt: null,
      overdue: false,
      reason: null,
      snapshot: null,
    };
  }

  // A big feed's data lives in Cache Storage behind a stub; read it back before the gate below
  // decides anything from `entry.data`, or a cached 7 MB feed would look like no feed at all.
  const entry = (await hydrate(id, readEntry(id))) || blankEntry();
  // `opts.now` exists for the contract test, which cannot wait out a five-minute gate.
  const now = Number.isFinite(opts.now) ? opts.now : wallNow();

  // Once per page, whatever the caches say: the status panel's "our snapshots" line must not
  // depend on some source happening to be due. A 4 kB file, HTTP-cached for a minute, and it
  // never rejects.
  ensureIndex().catch(() => {});

  // A row a browser cannot reach is no longer refused here: our snapshot is a route it may take,
  // and whether that route is open is decided per attempt, in doFetch. What differs is the gate.
  // An attempt that ended "could not look" touched nothing upstream, so it may look again on the
  // manifest's cadence rather than waiting out the source's; a harvester that comes back is then
  // seen within minutes, unattended.
  const sinceAttempt = entry.lastAttemptAt == null ? Infinity : now - entry.lastAttemptAt;
  const failed = entry.data == null;
  const gateMs = failed
    ? (entry.reason === 'no-route' ? INDEX_CADENCE_MS : emptyRetryMs(entry, src))
    : src.cadenceMs;
  let due = opts.force === true || sinceAttempt >= gateMs;

  // A cached FAILURE is not a reason to ignore our own bucket for three hours. MEASURED
  // 2026-09-08: a page whose live fetch had timed out kept saying "could not look" although a
  // usable snapshot had appeared in the manifest minutes later -- the source's cadence gated the
  // cheap same-origin check along with the expensive upstream one. So on the manifest's cadence,
  // a failed source asks the index whether a snapshot NEWER than its last attempt exists, and
  // only then is an attempt due. doFetch reads the snapshot first, so upstream is not touched
  // unless that snapshot proves unreadable.
  if (!due && failed && sinceAttempt >= INDEX_CADENCE_MS) {
    const view = await ensureIndex();
    const rid = src.registryId || src.id;
    const row = view.data && view.data.snapshots ? view.data.snapshots[rid] : null;
    const at = row && row.fetched_at ? Date.parse(row.fetched_at) : NaN;
    if (row && SNAPSHOT_USABLE.has(row.status) && Number.isFinite(at) && at > (entry.lastAttemptAt || 0)) due = true;
  }

  // OUR SNAPSHOT COMES BEFORE THE BROWSER'S COPY (data/readorder.js). A copy inside its cadence would be served
  // without looking at the bucket, so an hourly harvest would reach a returning visitor only after the cadence
  // (three to six hours for CelesTrak) had run out. So: when the manifest names a snapshot newer than the copy
  // held, read it now (one same-origin file; no publisher is asked), and when the manifest has not been read yet
  // this page, read it and do the same the moment it lands. A failed read does not repeat inside a minute.
  if (!due && entry.data != null && sinceAttempt >= RECHECK_MS) {
    const rid = src.registryId || src.id;
    const newer = () => {
      const v = indexView();
      const held = readEntry(id);
      return v.ok && snapshotNewerThanHeld(v.data.snapshots[rid], held ? held.fetchedAt : null, SNAPSHOT_USABLE);
    };
    if (newer()) {
      due = true;
    } else if (!indexState.data) {
      ensureIndex().then(() => { if (newer()) revalidate(id).catch(() => {}); }).catch(() => {});
    }
  }

  // Cache is inside the cadence window: serve it and do not touch the network.
  if (!due) {
    return resultFrom(src, readEntry(id) || entry, now, false);
  }

  // Due, and we have something to show: hand back the cached copy now, refresh behind it.
  if (entry.data != null && opts.await !== true) {
    const p = revalidate(id);
    const out = resultFrom(src, entry, now, true);
    // Swallow: revalidate() already records failure in the entry.
    p.catch(() => {});
    return out;
  }

  await revalidate(id);
  return resultFrom(src, readEntry(id) || blankEntry(), wallNow(), false);
}

function resultFrom(src, entry, now, revalidating) {
  const has = entry.data != null;
  const age = entry.fetchedAt == null ? Infinity : now - entry.fetchedAt;
  return {
    ok: has && entry.lastError == null && entry.reason == null,
    data: entry.data,
    fetchedAt: entry.fetchedAt,
    // The stale ladder is the source's own promise (freshnessMaxMs), unchanged by where the copy
    // came from. A snapshot past its valid_until is not folded into it and not hidden either:
    // `overdue` says so on its own.
    stale: !has || age > src.freshnessMaxMs,
    error: entry.lastError,
    fromCache: has,
    revalidating,
    via: has ? entry.via : null,
    validUntil: entry.validUntil,
    readAt: entry.readAt,
    overdue: has && entry.via === 'snapshot' && entry.validUntil != null && now > entry.validUntil,
    reason: entry.reason,
    snapshot: entry.snapshot,
  };
}

/**
 * One row per source, including the ones never fetched and the ones a browser cannot reach.
 * `state` is the three-way answer the status panel shows:
 *   'ok'            we have data and it is inside freshnessMaxMs
 *   'stale'         we have data and it is older than that — still drawn, stamped
 *   'could-not-look' we have nothing: never tried, tried and failed, or no route at all
 * `via` says where a copy came from ('snapshot' | 'live' | null), `reason` and `snapshot` say, as
 * codes, why there is none and why our snapshot was not it. `browser` is the registry's measured
 * flag: whether a page may read the host directly when the snapshot is missing.
 * @returns {Array<{id:string,label:string,publisher:string,fetchedAt:number|null,ageMs:number|null,
 *                  stale:boolean,error:string|null,attribution:string,browser:boolean,state:string,
 *                  attempted:boolean,lastAttemptAt:number|null,cadenceMs:number,url:string,
 *                  via:string|null,validUntil:number|null,readAt:number|null,overdue:boolean,
 *                  reason:string|null,snapshot:string|null}>}
 */
export function status() {
  const now = wallNow();
  return Object.values(SOURCES).map((src) => {
    const entry = readEntry(src.id) || blankEntry();
    const has = entry.data != null;
    const ageMs = entry.fetchedAt == null ? null : now - entry.fetchedAt;
    const stale = !has || ageMs > src.freshnessMaxMs;
    let state;
    if (!has) state = 'could-not-look';
    else if (stale) state = 'stale';
    else state = 'ok';
    return {
      id: src.id,
      label: src.label,
      publisher: src.publisher || src.label,
      fetchedAt: entry.fetchedAt,
      ageMs,
      stale,
      error: entry.lastError,
      attribution: src.attribution,
      browser: src.browser === true,
      state,
      attempted: entry.lastAttemptAt != null,
      lastAttemptAt: entry.lastAttemptAt,
      cadenceMs: src.cadenceMs,
      url: src.url,
      via: has ? entry.via : null,
      validUntil: entry.validUntil,
      readAt: entry.readAt,
      overdue: has && entry.via === 'snapshot' && entry.validUntil != null && now > entry.validUntil,
      reason: entry.reason,
      snapshot: entry.snapshot,
      // The saved copy is the answer on screen and the publisher is still being asked behind it
      // (spec 0060 task 3): the list says so, and stops saying so when the question is settled.
      pendingLive: has && entry.via === 'snapshot' && askingBehind.has(src.id),
    };
  });
}

const pendingListeners = new Set();
/** Called with (id) when a question asked behind a saved copy has been answered or given up. */
export function onPendingSettled(fn) {
  pendingListeners.add(fn);
  return () => pendingListeners.delete(fn);
}

/**
 * Called with (id, LoadResult) whenever a background revalidation finishes, succeed or fail.
 * Not in the module contract; without it a background refresh is invisible until the next load().
 * @param {(id: string, result: LoadResult) => void} fn
 * @returns {() => void} unsubscribe
 */
export function onUpdate(fn) {
  if (typeof fn !== 'function') return () => {};
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Drop one cached copy. Not in the module contract; the status panel's retry needs it. */
export function forget(id) {
  memory.delete(id);
  bulkDelete(id);
  const s = storage();
  if (!s) return;
  try {
    s.removeItem(PREFIX + id);
  } catch {
    /* nothing to do */
  }
}

/** Drop the cached manifest, so the next attempt re-reads it. A retry that keeps the old manifest
 *  would keep the old answer. In memory only: the manifest is never written to localStorage,
 *  because a page that starts with yesterday's manifest would trust yesterday's files. */
export function forgetIndex() {
  indexState.data = null;
  indexState.readAt = null;
  indexState.lastAttemptAt = null;
  indexState.error = null;
  indexState.httpStatus = null;
}

/**
 * What the harvester's manifest last said, for the status panel. Reads the cache; never fetches.
 *   checked      we have tried to read the manifest this page
 *   available    a schema-1 manifest is in hand (kept across a failed re-read; the FILES decide)
 *   generatedAt  the manifest's generated_at as wall ms, and generatedAgeMs against the wall now
 *   counts       one per manifest status, plus `good` = ok + not-modified + not-due, the rows a
 *                snapshot file exists for
 * @returns {{checked:boolean, available:boolean, generatedAt:number|null, generatedAgeMs:number|null,
 *            readAt:number|null, runner:string|null, error:string|null, httpStatus:number|null,
 *            counts:{ok:number,notModified:number,notDue:number,refused:number,error:number,
 *                    skipped:number,other:number,good:number,total:number}}}
 */
/**
 * Does the harvester's manifest hold a usable snapshot for this source right now? One index
 * fetch, cached, shared with load(). main.js uses it to start snapshot-backed layers at once
 * instead of queueing them behind upstream hosts that may be slow.
 */
export async function snapshotAvailable(id) {
  const src = SOURCES[id];
  if (!src) return false;
  const view = await ensureIndex();
  const rid = src.registryId || src.id;
  const row = view.data && view.data.snapshots ? view.data.snapshots[rid] : null;
  return !!(row && SNAPSHOT_USABLE.has(row.status) && row.fetched_at);
}

export function harvestStatus() {
  const doc = indexState.data;
  const counts = {
    ok: 0, notModified: 0, notDue: 0, refused: 0, error: 0, skipped: 0, other: 0, good: 0, total: 0,
  };
  if (doc) {
    for (const row of Object.values(doc.snapshots)) {
      counts.total += 1;
      const s = row && typeof row === 'object' ? row.status : null;
      if (s === 'ok') counts.ok += 1;
      else if (s === 'not-modified') counts.notModified += 1;
      else if (s === 'not-due') counts.notDue += 1;
      else if (s === 'refused') counts.refused += 1;
      else if (s === 'error') counts.error += 1;
      else if (s === 'skipped') counts.skipped += 1;
      else counts.other += 1;
    }
    counts.good = counts.ok + counts.notModified + counts.notDue;
  }
  const generatedAt = doc ? Date.parse(doc.generated_at) : NaN;
  const hasGenerated = Number.isFinite(generatedAt);
  return {
    checked: indexState.lastAttemptAt != null,
    available: doc != null,
    generatedAt: hasGenerated ? generatedAt : null,
    generatedAgeMs: hasGenerated ? Math.max(0, wallNow() - generatedAt) : null,
    readAt: indexState.readAt,
    runner: doc && doc.run && typeof doc.run.runner === 'string' ? doc.run.runner : null,
    error: indexState.error,
    httpStatus: indexState.httpStatus,
    counts,
  };
}

// --- fetching ---------------------------------------------------------------------------------

function revalidate(id) {
  const existing = inflight.get(id);
  if (existing) return existing;
  const p = doFetch(id).finally(() => inflight.delete(id));
  inflight.set(id, p);
  return p;
}

async function doFetch(id) {
  const src = SOURCES[id];
  const prev = readEntry(id) || blankEntry();
  const attemptAt = wallNow();
  // Record the attempt BEFORE the request resolves. If the tab is closed mid-flight the next
  // load still sees an attempt and waits out the cadence, which is what keeps a flaky network
  // from turning into a retry loop against CelesTrak.
  writeEntry(id, { ...prev, lastAttemptAt: attemptAt });

  // 1. Our snapshot, when the manifest says there is one to read.
  const snap = await readSnapshot(src);
  const canAskOriginal = src.browser === true;
  // `cutOf`: a cut's live URL is the whole feed, so a stale cut is not refreshed behind itself (header).
  const plan = decide({ snapshot: snap.ok ? { validUntil: snap.validUntil } : null, now: attemptAt,
    canAskOriginal, canAskBehind: canAskOriginal && !src.cutOf });
  let next;
  let behind = false;
  if (plan.use === 'snapshot') {
    // Never an older copy over a newer one: a browser that read the publisher itself after the harvester did
    // keeps what it holds (and the publisher is still asked behind a snapshot that is past its valid_until).
    const keepHeld = prev.data != null && !newerThanHeld(snap.fetchedAt, prev.fetchedAt);
    next = keepHeld
      ? { ...prev, lastAttemptAt: attemptAt, lastError: null, lastStatus: snap.httpStatus, reason: null, snapshot: null }
      : {
        data: snap.body,
        // The harvester's read time, not ours: the age the panel shows is the age of the data.
        fetchedAt: snap.fetchedAt,
        lastAttemptAt: attemptAt,
        lastError: null,
        lastStatus: snap.httpStatus,
        via: 'snapshot',
        validUntil: snap.validUntil,
        readAt: attemptAt,
        reason: null,
        snapshot: null,
      };
    // Past its own valid_until: draw it now, and ask upstream behind it (see the header).
    behind = plan.askBehind;
  } else if (plan.use === 'original') {
    // 2. The direct fetch this file has always made. The safety net, unchanged. When it fails too, the copy
    //    the browser holds stays (fetchLive keeps `prev.data`): step 3, the last resort.
    next = await fetchLive(src, prev, attemptAt);
    next.snapshot = snap.why;
  } else {
    // 3. Could not look. No snapshot and no route a browser may take, so nothing is requested and
    //    nothing is invented. A copy from an earlier snapshot is kept: it is data, merely old.
    next = {
      ...prev,
      lastAttemptAt: attemptAt,
      lastError: null,
      lastStatus: snap.httpStatus,
      reason: 'no-route',
      snapshot: snap.why,
    };
  }
  // Consecutive attempts that produced nothing, for the retry floor above. A success clears it.
  next.failures = next.data == null ? (prev.failures || 0) + 1 : 0;
  writeEntry(id, next);

  const result = resultFrom(src, next, wallNow(), false);
  notify(id, result);
  if (behind) liveBehind(id, src, next);
  return result;
}

function notify(id, result) {
  for (const fn of listeners) {
    try {
      fn(id, result);
    } catch {
      /* a listener must not be able to break the data layer */
    }
  }
}

/**
 * The publisher, asked behind a saved copy that is already on screen. A newer answer replaces the
 * copy and is announced; anything else -- a refusal, a timeout, the same file -- leaves the copy
 * exactly as it was, with no second error on the panel for a source that is showing data.
 */
// The sources whose publisher is being asked behind a saved copy right now (status().pendingLive).
const askingBehind = new Set();

function liveBehind(id, src, shown) {
  askingBehind.add(id);
  // Told once the copy is on screen and again when the publisher has answered or given up, so the
  // list's "checking for a newer one" never outlives the question (spec 0060 task 3).
  const settle = () => { askingBehind.delete(id); for (const fn of pendingListeners) { try { fn(id); } catch { /* a listener cannot break the data layer */ } } };
  fetchLive(src, shown, wallNow())
    .finally(settle)
    .then((live) => {
      if (live.via !== 'live' || !(live.fetchedAt > (shown.fetchedAt || 0))) return;
      const entry = { ...live, failures: 0 };
      writeEntry(id, entry);
      notify(id, resultFrom(src, entry, wallNow(), false));
    })
    .catch(() => {});
}

/**
 * Today's direct upstream fetch, unchanged in what it does: one request, CelesTrak's 403 read as
 * "not yet updated", an empty answer read as no answer. Returns the next Entry; never throws.
 */
async function fetchLive(src, prev, attemptAt) {
  let error = null;
  let httpStatus = null;
  let payload = null;

  try {
    // A dead host must cost seconds, not minutes. MEASURED 2026-09-08: with CelesTrak not
    // answering, each of its files hung for 75 s before the browser gave up, and the map sat empty
    // behind them. Twenty seconds is longer than any of these sources takes when it is alive (the
    // 7 MB catalogue included, measured at 3.7 s) and short enough to move on.
    const abort = new AbortController();
    const timer = setTimeout(() => abort.abort(), LIVE_TIMEOUT_MS);
    let response;
    try {
      response = await fetch(src.url, { credentials: 'omit', redirect: 'follow', signal: abort.signal });
    } finally {
      clearTimeout(timer);
    }
    httpStatus = response.status;
    if (response.status === 403 && src.id.startsWith('celestrak-')) {
      // The documented answer to an early re-fetch. It is not a failure and it must never be
      // retried: fifty HTTP errors in two hours gets the client firewalled.
      error = 'CelesTrak has not published a new file since our last download.';
    } else if (!response.ok) {
      error = `The server answered ${response.status}.`;
    } else {
      const text = await response.text();
      if (src.kind === 'json') {
        payload = JSON.parse(text);
      } else {
        payload = text;
      }
      if (payload == null || (Array.isArray(payload) && payload.length === 0)) {
        error = 'The server answered, but with nothing in it.';
        payload = null;
      }
    }
  } catch (e) {
    error = e && e.name === 'AbortError' ? `No answer within ${LIVE_TIMEOUT_MS / 1000} seconds.` : describe(e);
  }

  const got = payload != null;
  // Step 3 of data/readorder.js: when the publisher gave nothing either, what the browser holds stays.
  const stands = lastResort({ got, hasCache: prev.data != null });
  return {
    data: stands === 'none' ? null : got ? payload : prev.data,
    fetchedAt: got ? attemptAt : prev.fetchedAt,
    lastAttemptAt: attemptAt,
    lastError: got ? null : error,
    lastStatus: httpStatus,
    via: got ? 'live' : prev.via,
    validUntil: got ? null : prev.validUntil,
    readAt: got ? attemptAt : prev.readAt,
    reason: got ? null : 'live-failed',
    snapshot: null,
  };
}

// --- the harvester's snapshots -----------------------------------------------------------------

/** The manifest, in memory for this page only. See forgetIndex() for why it is not persisted. */
const indexState = {
  data: null,
  readAt: null,
  lastAttemptAt: null,
  error: null,
  httpStatus: null,
  inflight: null,
};

function indexView() {
  return {
    ok: indexState.data != null,
    data: indexState.data,
    error: indexState.error,
    httpStatus: indexState.httpStatus,
  };
}

/** The manifest, re-read at most once per INDEX_CADENCE_MS, one request in flight. Never rejects. */
function ensureIndex() {
  if (indexState.inflight) return indexState.inflight;
  const now = wallNow();
  const since = indexState.lastAttemptAt == null ? Infinity : now - indexState.lastAttemptAt;
  if (since < INDEX_CADENCE_MS) return Promise.resolve(indexView());
  indexState.lastAttemptAt = now;
  indexState.inflight = fetchIndex().finally(() => {
    indexState.inflight = null;
  });
  return indexState.inflight;
}

async function fetchIndex() {
  let httpStatus = null;
  try {
    const response = await fetch(SNAPSHOT_INDEX_URL, { credentials: 'omit' });
    httpStatus = response.status;
    if (!response.ok) throw new Error(`The server answered ${response.status}.`);
    const doc = JSON.parse(await response.text());
    if (!doc || doc.schema !== SNAPSHOT_SCHEMA || !doc.snapshots || typeof doc.snapshots !== 'object') {
      throw new Error(`The snapshot index is not schema ${SNAPSHOT_SCHEMA}.`);
    }
    indexState.data = doc;
    indexState.readAt = wallNow();
    indexState.error = null;
  } catch (e) {
    // A failed re-read keeps the manifest we had: the files, not the manifest, decide whether a
    // snapshot is really there, and a network blip must not flip every source to live at once.
    indexState.error = describe(e);
  }
  indexState.httpStatus = httpStatus;
  return indexView();
}

/**
 * Read /data/v1/<registry-id>.json when the manifest says a file exists for it. Never throws.
 * The manifest is keyed by the REGISTRY id (`celestrak-supplemental-starlink`), which is not
 * always this module's id (`celestrak-starlink`); a source with no registry row has no snapshot.
 * @returns {Promise<{ok:boolean, body?:*, fetchedAt?:number, validUntil?:number|null,
 *                    httpStatus:number|null, why:string|null}>}
 *   why: 'no-index' | 'not-in-index' | 'refused' | 'error' | 'skipped' | 'unreadable' | null
 */
async function readSnapshot(src) {
  const rid = src.registryId || src.id;
  const index = await ensureIndex();
  if (!index.ok) return { ok: false, httpStatus: index.httpStatus, why: 'no-index' };
  const row = index.data.snapshots[rid];
  if (!row || typeof row !== 'object') return { ok: false, httpStatus: null, why: 'not-in-index' };
  if (!SNAPSHOT_USABLE.has(row.status) || !row.fetched_at) {
    const why = row.status === 'refused' || row.status === 'error' || row.status === 'skipped'
      ? row.status
      : 'unreadable';
    return { ok: false, httpStatus: null, why };
  }

  // AS COLUMNS FIRST, WHEN THE MANIFEST NAMES SUCH A FILE (internal #523; scripts/columnar.py,
  // data/columnar.js). The big catalogues repeat 17 keys in each of 16 000 rows; the column file
  // holds the same rows in about a third of the text. It is an extra: anything wrong with it (not
  // there, not JSON, a column that does not add up) and the verbatim file below is read exactly as
  // before. The decoded rows are the verbatim rows, so nothing after this line can tell.
  const viaColumns = await readColumns(src, row);
  if (viaColumns) return viaColumns;

  let httpStatus = null;
  try {
    const response = await fetch(SNAPSHOT_BASE + encodeURIComponent(rid) + '.json', {
      credentials: 'omit',
    });
    httpStatus = response.status;
    if (!response.ok) return { ok: false, httpStatus, why: 'unreadable' };
    const file = JSON.parse(await response.text());
    if (!file || file.schema !== SNAPSHOT_SCHEMA || file.body == null) {
      return { ok: false, httpStatus, why: 'unreadable' };
    }
    // VERBATIM pass-through (§3): parsed JSON for a JSON upstream, the text otherwise. A JSON
    // body that arrived as text is parsed here so the parser sees exactly what a live fetch gives.
    let body = file.body;
    if (src.kind === 'json' && typeof body === 'string') body = JSON.parse(body);
    if (body == null || body === '' || (Array.isArray(body) && body.length === 0)) {
      return { ok: false, httpStatus, why: 'unreadable' };
    }
    const fetchedAt = Date.parse(file.fetched_at || row.fetched_at);
    if (!Number.isFinite(fetchedAt)) return { ok: false, httpStatus, why: 'unreadable' };
    const validUntil = Date.parse(file.valid_until || row.valid_until);
    return {
      ok: true,
      body,
      fetchedAt,
      validUntil: Number.isFinite(validUntil) ? validUntil : null,
      httpStatus,
      why: null,
    };
  } catch {
    return { ok: false, httpStatus, why: 'unreadable' };
  }
}

/** The column twin of a saved copy, decoded to its rows; null for "read the verbatim file". */
async function readColumns(src, row) {
  const twin = row.columns;
  if (src.kind !== 'json' || !twin || typeof twin.path !== 'string' || !/^[\w.-]+\.cols\.json$/.test(twin.path)) return null;
  try {
    const response = await fetch(SNAPSHOT_BASE + encodeURIComponent(twin.path), { credentials: 'omit' });
    if (!response.ok) return null;
    const file = JSON.parse(await response.text());
    if (!file || file.schema !== SNAPSHOT_SCHEMA) return null;
    // The twin of THIS publish, not a file an edge kept from the last one: the manifest row and
    // the file must agree on when the publisher was read and on how many rows there are.
    if ((file.fetched_at || null) !== (row.fetched_at || null) || file.rows !== twin.rows) return null;
    const { decodeColumns } = await import('./columnar.js');
    const body = decodeColumns(file);
    const fetchedAt = Date.parse(file.fetched_at);
    if (!Number.isFinite(fetchedAt)) return null;
    const validUntil = Date.parse(file.valid_until || row.valid_until);
    return { ok: true, body, fetchedAt, validUntil: Number.isFinite(validUntil) ? validUntil : null, httpStatus: response.status, why: null };
  } catch {
    return null;
  }
}

function describe(e) {
  if (!e) return 'Something went wrong and said nothing about it.';
  const msg = String(e.message || e);
  if (/Failed to fetch|NetworkError|Load failed/i.test(msg)) {
    return 'Could not reach the server — no network, or the browser blocked the request.';
  }
  if (/JSON/i.test(msg)) return 'The server answered with something that was not the JSON we expect.';
  return msg;
}
